
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useScan } from "../context/ScanContext";
import {
  Box,
  Typography,
  Stack,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RadarIcon from "@mui/icons-material/Radar";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HttpOutlinedIcon from "@mui/icons-material/HttpOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import DashboardLayout from "../layouts/DashboardLayout";
import { COLORS, SHADOW } from "../theme/tokens";
import FlatCard from "../components/FlatCard";
import TableSkeleton from "../components/TableSkeleton";
import { useToast } from "../components/ToastProvider";
import api from "../api";
import { useMonitors } from "../hooks/queries";
import { HEADING_FONT } from "../theme/fonts";

// Turn the /domain-monitors response into { rows, total, counts } for the
// current page. The backend normally returns { rows, total, counts }; if an
// older build returns a bare array, we paginate + tally on the client so the
// page still works during a partial deploy.
const EMPTY_COUNTS = { all: 0, up: 0, down: 0, withIssues: 0 };

function normalizeMonitors(data, page, pageSize, debouncedSearch, filter) {
  if (!data) return { rows: [], total: 0, counts: EMPTY_COUNTS };
  if (Array.isArray(data)) {
    const tally = { all: data.length, up: 0, down: 0, withIssues: 0 };
    data.forEach((m) => {
      if (m.isUp) tally.up++;
      else tally.down++;
      if ((m.issues || []).length > 0) tally.withIssues++;
    });
    const fil = data.filter((m) => {
      const matchesSearch = (m.domainName || "")
        .toLowerCase()
        .includes(debouncedSearch.toLowerCase());
      const hasIssues = (m.issues || []).length > 0;
      const matchesFilter =
        filter === "all" ||
        (filter === "up" && m.isUp) ||
        (filter === "down" && !m.isUp) ||
        (filter === "issues" && hasIssues);
      return matchesSearch && matchesFilter;
    });
    const start = (page - 1) * pageSize;
    return {
      rows: fil.slice(start, start + pageSize),
      total: fil.length,
      counts: tally,
    };
  }
  return {
    rows: data.rows || [],
    total: data.total || 0,
    counts: data.counts || EMPTY_COUNTS,
  };
}

function formatRelative(dateStr) {
  if (!dateStr) return "—";
  const then = new Date(dateStr.replace(" ", "T"));
  if (isNaN(then.getTime())) return dateStr;
  const diffMs = Date.now() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  const diffMo = Math.floor(diffD / 30);
  return `${diffMo}mo ago`;
}

// A single labelled metric inside an expanded monitor row.
function DetailTile({ icon, label, value, color, mono, colors }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ color: colors.textMuted, mb: 0.5 }}
      >
        {icon}
        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: color || colors.text,
          fontFamily: mono
            ? "ui-monospace, SFMono-Regular, Menlo, monospace"
            : HEADING_FONT,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function DomainMonitors() {
  const navigate = useNavigate();
  // Flat spec design tokens (light-only). Replaces the old glass theme
  // (useThemeColors) so the page is locked to the "Blue · Light" system.
  const colors = {
    text: COLORS.ink,
    textMuted: COLORS.muted,
    textFaint: COLORS.faint,
    cardBg: COLORS.surface,
    menuBg: COLORS.surface,
    cardBorder: COLORS.hairline,
    cardBorderHover: COLORS.borderHover,
    hover: COLORS.tile,
    divider: COLORS.hairline,
    shadowLg: SHADOW.modal,
  };
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // Scan All button. Set to false to hide it and fall back to the per-row
  // "Re-scan" (single-domain) button during testing.
  const SHOW_SCAN_ALL = true;
  // Which domain row is currently being re-scanned (per-row spinner).
  const [scanningId, setScanningId] = useState(null);

  // Scan state is global (header shows it on every page), so we read it from the
  // ScanContext instead of polling here. `running` drives the page's banner +
  // button; the completion toast + list refresh are handled globally.
  const {
    running: scanning,
    cancelling,
    progress: scanProgress,
    startScan,
    cancelScan,
  } = useScan();

  const [search, setSearch] = useState("");
  // Debounced copy of `search` so we hit the API after the user stops typing,
  // not on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Honour a ?filter=up|down|issues deep-link (e.g. from the Dashboard's
  // Live Monitoring tiles); default to "all" for anything else.
  const initialFilter = ["up", "down", "issues"].includes(
    searchParams.get("filter")
  )
    ? searchParams.get("filter")
    : "all";
  const [filter, setFilter] = useState(initialFilter);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Build the query params from the active page + filters. React Query
  // re-fetches automatically whenever this object changes.
  const params = useMemo(() => {
    const p = { limit: pageSize, offset: (page - 1) * pageSize };
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    if (filter !== "all") p.status = filter;
    return p;
  }, [page, pageSize, debouncedSearch, filter]);

  // placeholderData keeps the previous page on screen while the next one loads.
  const monitorsQuery = useMonitors(params, { placeholderData: (prev) => prev });
  const { rows: monitors, total, counts } = useMemo(
    () => normalizeMonitors(monitorsQuery.data, page, pageSize, debouncedSearch, filter),
    [monitorsQuery.data, page, pageSize, debouncedSearch, filter]
  );
  const loading = monitorsQuery.isFetching;

  // Debounce the search box → debouncedSearch.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Start an on-demand scan of every domain. The scan runs in the BACKGROUND on
  // the server (it takes minutes); ScanContext tracks progress globally and
  // fires the completion toast + list refresh, so here we just kick it off.
  async function handleScan() {
    if (scanning) return;
    try {
      await startScan();
    } catch (err) {
      toast.error(err.message || "Scan failed. Please try again.");
    }
  }

  // Ask the server to stop the in-flight scan. It halts after the current batch.
  async function handleCancelScan() {
    if (!scanning || cancelling) return;
    try {
      await cancelScan();
      toast.info?.("Cancelling scan…");
    } catch (err) {
      toast.error(err.message || "Couldn't cancel the scan.");
    }
  }

  // Re-scan a SINGLE domain on demand (testing). Hits the per-domain endpoint,
  // then refreshes the list so the row shows the fresh result.
  async function handleScanOne(domainId) {
    if (!domainId || scanningId) return;
    setScanningId(domainId);
    try {
      await api.post(`/domain-monitors/${domainId}/scan`);
      queryClient.invalidateQueries({ queryKey: ["monitors"] });
      toast.success("Domain re-scanned");
    } catch (err) {
      toast.error(err.message || "Scan failed. Please try again.");
    } finally {
      setScanningId(null);
    }
  }

  // Reset to page 1 when the (debounced) search, filter, or page size changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  // If the current page falls past the end (e.g. after a scan shrinks a
  // filtered set), step back so we never show an empty page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // `monitors` is already just the current page from the backend.
  const pagedMonitors = monitors;
  const startIndex = (safePage - 1) * pageSize;
  const rangeStart = total === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + pageSize, total);

  function buildPageList(current, total) {
    const pages = new Set([1, total, current, current - 1, current + 1]);
    const sorted = [...pages]
      .filter((n) => n >= 1 && n <= total)
      .sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const n of sorted) {
      if (n - prev > 1) out.push("...");
      out.push(n);
      prev = n;
    }
    return out;
  }
  const pageList = buildPageList(safePage, totalPages);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: 2,
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
    },
    "& input": { color: colors.text },
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4, justifyContent: "space-between" }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              color: colors.text,
              letterSpacing: "-0.5px",
            }}
          >
            Domain Monitors
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textMuted, mt: 0.5 }}
          >
            {counts.all} monitor
            {counts.all === 1 ? "" : "s"} · uptime, SSL, DNS & content checks
          </Typography>
        </Box>

        {SHOW_SCAN_ALL && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignSelf: { sm: "flex-end" } }}
        >
        <Button
          onClick={handleScan}
          disabled={scanning}
          startIcon={
            scanning ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              <RadarIcon />
            )
          }
          sx={{
            // flexShrink: 0,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 15,
            lineHeight: 1,
            px: 2.25,
            pt: 0.25,
            pb: 0.25,
            minWidth: 0,
            minHeight: 0,
            height: 38,
            alignSelf: { sm: "flex-end" },
            borderRadius: 2,
            "& .MuiButton-startIcon > *:nth-of-type(1)": {
              fontSize: 16,
            },
            color: "#fff",
            background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
            boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
            "&:hover": {
              boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)",
              transform: "translateY(-1px)",
            },
            "&.Mui-disabled": {
              color: "rgba(255,255,255,0.7)",
              background:
                "linear-gradient(135deg, rgba(79,70,229,0.55) 0%, rgba(124,58,237,0.55) 100%)",
            },
            transition: "all 0.15s ease",
          }}
        >
          {scanning ? "Scanning…" : "Scan All"}
        </Button>

        {scanning && (
          <Button
            onClick={handleCancelScan}
            disabled={cancelling}
            startIcon={
              cancelling ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <StopCircleOutlinedIcon />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 15,
              lineHeight: 1,
              px: 2.25,
              pt: 0.25,
              pb: 0.25,
              minWidth: 0,
              minHeight: 0,
              height: 38,
              borderRadius: 2,
              "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 16 },
              color: "#fff",
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              boxShadow: "0 4px 14px rgba(239, 68, 68, 0.35)",
              "&:hover": {
                boxShadow: "0 8px 22px rgba(239, 68, 68, 0.45)",
                transform: "translateY(-1px)",
              },
              "&.Mui-disabled": {
                color: "#fff",
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                opacity: 0.85,
              },
              transition: "all 0.15s ease",
            }}
          >
            {cancelling ? "Cancelling…" : "Cancel"}
          </Button>
        )}
        </Stack>
        )}
      </Stack>

      {/* Live scan progress banner */}
      {scanning && (
        <FlatCard
          sx={{
            p: 2.5,
            mb: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={24} sx={{ color: "#3B82F6" }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="baseline"
              justifyContent="space-between"
              sx={{ mb: 0.25 }}
            >
              <Typography
                sx={{ fontSize: 14, fontWeight: 700, color: colors.text }}
              >
                {scanProgress?.phase === "retrying"
                  ? "Retrying domains…"
                  : "Scanning all domains…"}
              </Typography>
              {scanProgress?.total ? (
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, color: "#3B82F6" }}
                >
                  {scanProgress.checked}/{scanProgress.total}
                </Typography>
              ) : null}
            </Stack>
            <Typography
              sx={{ fontSize: 12.5, color: colors.textMuted, mb: 1 }}
            >
              Checking uptime, SSL &amp; DNS. You can keep using the app — live
              status stays in the header.
            </Typography>
            <LinearProgress
              variant={scanProgress?.total ? "determinate" : "indeterminate"}
              value={
                scanProgress?.total
                  ? Math.min(
                      100,
                      Math.round(
                        (scanProgress.checked / scanProgress.total) * 100
                      )
                    )
                  : undefined
              }
              sx={{
                borderRadius: 1,
                height: 6,
                background: colors.hover,
                "& .MuiLinearProgress-bar": {
                  background: "linear-gradient(90deg, #3B82F6, #1D4ED8)",
                },
              }}
            />
          </Box>
        </FlatCard>
      )}

      {/* Filter + Search Bar */}
      <FlatCard
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: { md: "center" },
        }}
      >
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, value) => value && setFilter(value)}
          sx={{
            // Mobile: keep all tabs (incl. "Has Issues") on one line and scroll
            // sideways instead of wrapping. Desktop (md+) keeps the wrap.
            maxWidth: "100%",
            flexWrap: { xs: "nowrap", md: "wrap" },
            overflowX: { xs: "auto", md: "visible" },
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontSize: 13,
              fontWeight: 600,
              px: 2,
              py: 0.75,
              flexShrink: 0,
              whiteSpace: "nowrap",
              border: `1px solid ${colors.cardBorder}`,
              color: colors.textMuted,
              "&:hover": { background: colors.hover },
              "&.Mui-selected": {
                background: "rgba(59, 130, 246, 0.12)",
                color: "#3B82F6",
                borderColor: "#3B82F6",
                "&:hover": { background: "rgba(59, 130, 246, 0.18)" },
              },
            },
          }}
        >
          <ToggleButton value="all">
            All
            <Chip
              size="small"
              label={counts.all}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: colors.hover,
                color: colors.textMuted,
              }}
            />
          </ToggleButton>
          <ToggleButton value="up">
            Up
            <Chip
              size="small"
              label={counts.up}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(16, 185, 129, 0.15)",
                color: "#10b981",
              }}
            />
          </ToggleButton>
          <ToggleButton value="down">
            Down
            <Chip
              size="small"
              label={counts.down}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(239, 68, 68, 0.15)",
                color: "#ef4444",
              }}
            />
          </ToggleButton>
          <ToggleButton value="issues">
            Has Issues
            <Chip
              size="small"
              label={counts.withIssues}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(245, 158, 11, 0.15)",
                color: "#f59e0b",
              }}
            />
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder="Search domain..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            ...fieldSx,
            flex: 1,
            "& .MuiOutlinedInput-root": {
              ...fieldSx["& .MuiOutlinedInput-root"],
              fontSize: 13,
            },
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{ color: colors.textFaint, mr: 1, fontSize: 18 }}
              />
            ),
          }}
        />
      </FlatCard>

      {/* Empty state */}
      {monitors.length === 0 && loading ? (
        <FlatCard sx={{ overflow: "hidden" }}>
          <TableSkeleton rows={pageSize || 8} columns={8} />
        </FlatCard>
      ) : monitors.length === 0 ? (
        <FlatCard sx={{ py: 8, textAlign: "center" }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#3B82F6",
              background: "rgba(59, 130, 246, 0.12)",
              border: "1px solid rgba(59, 130, 246, 0.25)",
            }}
          >
            <MonitorHeartOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: HEADING_FONT,
              fontSize: 16,
              fontWeight: 700,
              color: colors.text,
              mb: 0.5,
            }}
          >
            {counts.all === 0 ? "No monitor data yet" : "No matches found"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
            Try a different filter or search
          </Typography>
        </FlatCard>
      ) : (
        <FlatCard sx={{ overflow: "hidden" }}>
          {/* Accordion rows */}
          {pagedMonitors.map((m) => {
            const upColor = m.isUp ? "#10b981" : "#ef4444";
            const upBg = m.isUp
              ? "rgba(16, 185, 129, 0.12)"
              : "rgba(239, 68, 68, 0.12)";
            const upGradient = m.isUp
              ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
              : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)";
            const issueCount = (m.issues || []).length;
            const httpColor =
              m.httpStatus === null
                ? colors.textFaint
                : m.httpStatus >= 200 && m.httpStatus < 400
                ? "#10b981"
                : "#ef4444";
            const href = m.domainName
              ? m.domainName.startsWith("http")
                ? m.domainName
                : `https://${m.domainName}`
              : "#";
            const sslText =
              m.sslValid === null
                ? "Not checked"
                : m.sslValid
                ? m.sslDaysLeft !== null
                  ? `${m.sslDaysLeft}d left`
                  : "Valid"
                : "Invalid";
            const sslColor =
              m.sslValid === null
                ? colors.textFaint
                : m.sslValid
                ? "#10b981"
                : "#ef4444";

            return (
              <Accordion
                key={m.id}
                disableGutters
                elevation={0}
                square
                sx={{
                  background: "transparent",
                  borderBottom: `1px solid ${colors.divider}`,
                  "&:last-of-type": { borderBottom: "none" },
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { background: colors.hover },
                  transition: "background 0.15s ease",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{ color: colors.textMuted, fontSize: 22 }}
                    />
                  }
                  sx={{
                    px: { xs: 1.5, sm: 3 },
                    "&:hover": { background: colors.hover },
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      gap: 1.5,
                      my: 1.25,
                      minWidth: 0,
                    },
                  }}
                >
                  {/* Status icon */}
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      background: upGradient,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: `0 4px 12px ${upColor}40`,
                    }}
                  >
                    {m.isUp ? (
                      <CheckCircleOutlinedIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <ErrorOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                    )}
                  </Box>

                  {/* Name + last checked */}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontSize: 14.5,
                        fontWeight: 700,
                        color: colors.text,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {m.domainName || "(unknown)"}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontSize: 11.5,
                        color: colors.textMuted,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Checked {formatRelative(m.lastChecked)}
                    </Typography>
                  </Box>

                  {/* Summary chips (hidden on small screens) */}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ display: { xs: "none", md: "flex" }, flexShrink: 0 }}
                  >
                    <Chip
                      size="small"
                      label={m.httpStatus ?? "no HTTP"}
                      sx={{
                        height: 24,
                        fontSize: 11.5,
                        fontWeight: 700,
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, monospace",
                        color: httpColor,
                        background: colors.hover,
                        border: `1px solid ${colors.cardBorder}`,
                      }}
                    />
                    <Chip
                      size="small"
                      label={sslText}
                      sx={{
                        fontFamily: HEADING_FONT,
                        height: 24,
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: sslColor,
                        background:
                          m.sslValid === false
                            ? "rgba(239,68,68,0.12)"
                            : m.sslValid
                            ? "rgba(16,185,129,0.12)"
                            : colors.hover,
                        border: `1px solid ${
                          m.sslValid === false
                            ? "#ef444433"
                            : m.sslValid
                            ? "#10b98133"
                            : colors.cardBorder
                        }`,
                      }}
                    />
                  </Stack>

                  {/* Issues count */}
                  {issueCount > 0 && (
                    <Chip
                      size="small"
                      icon={
                        <WarningAmberOutlinedIcon
                          sx={{ fontSize: 14, color: "#f59e0b !important" }}
                        />
                      }
                      label={issueCount}
                      sx={{
                        fontFamily: HEADING_FONT,
                        flexShrink: 0,
                        height: 24,
                        fontWeight: 700,
                        fontSize: 11.5,
                        color: "#f59e0b",
                        background: "rgba(245, 158, 11, 0.12)",
                        border: "1px solid rgba(245, 158, 11, 0.3)",
                        "& .MuiChip-icon": { ml: "6px" },
                      }}
                    />
                  )}

                  {/* Status */}
                  <Chip
                    size="small"
                    label={m.isUp ? "Up" : "Down"}
                    sx={{
                      fontFamily: HEADING_FONT,
                      flexShrink: 0,
                      fontWeight: 700,
                      fontSize: 11,
                      color: upColor,
                      background: upBg,
                      border: `1px solid ${upColor}33`,
                      width: 58,
                    }}
                  />
                </AccordionSummary>

                <AccordionDetails sx={{ px: { xs: 1.5, sm: 3 }, pt: 0, pb: 2.5 }}>
                  {/* Detail metrics */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2, 1fr)",
                        sm: "repeat(3, 1fr)",
                        md: "repeat(5, 1fr)",
                      },
                      gap: 2,
                      p: 2,
                      mb: issueCount > 0 ? 2 : 0,
                      borderRadius: 2,
                      background: colors.cardBg,
                      border: `1px solid ${colors.cardBorder}`,
                    }}
                  >
                    <DetailTile
                      icon={<HttpOutlinedIcon sx={{ fontSize: 15 }} />}
                      label="HTTP"
                      value={m.httpStatus ?? "—"}
                      color={httpColor}
                      mono
                      colors={colors}
                    />
                    <DetailTile
                      icon={<SpeedOutlinedIcon sx={{ fontSize: 15 }} />}
                      label="Response"
                      value={
                        m.responseTimeMs !== null
                          ? `${m.responseTimeMs}ms`
                          : "—"
                      }
                      color={
                        m.responseTimeMs === null
                          ? colors.textFaint
                          : m.responseTimeMs > 1000
                          ? "#f59e0b"
                          : colors.text
                      }
                      mono
                      colors={colors}
                    />
                    <DetailTile
                      icon={<LockOutlinedIcon sx={{ fontSize: 15 }} />}
                      label="SSL"
                      value={sslText}
                      color={sslColor}
                      colors={colors}
                    />
                    <DetailTile
                      icon={<DnsOutlinedIcon sx={{ fontSize: 15 }} />}
                      label="DNS"
                      value={
                        m.dnsResolves === null
                          ? "—"
                          : m.dnsResolves
                          ? "Resolves"
                          : "Fails"
                      }
                      color={
                        m.dnsResolves === null
                          ? colors.textFaint
                          : m.dnsResolves
                          ? "#10b981"
                          : "#ef4444"
                      }
                      colors={colors}
                    />
                    <DetailTile
                      icon={<AccessTimeOutlinedIcon sx={{ fontSize: 15 }} />}
                      label="Last check"
                      value={formatRelative(m.lastChecked)}
                      color={colors.text}
                      colors={colors}
                    />
                  </Box>

                  {/* Issues list */}
                  {issueCount > 0 && (
                    <Stack spacing={1}>
                      {(m.issues || []).map((issue, i) => (
                        <Stack
                          key={i}
                          direction="row"
                          spacing={1.5}
                          alignItems="flex-start"
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: "rgba(245, 158, 11, 0.08)",
                            border: "1px solid rgba(245, 158, 11, 0.22)",
                          }}
                        >
                          <WarningAmberOutlinedIcon
                            sx={{
                              fontSize: 16,
                              color: "#f59e0b",
                              flexShrink: 0,
                              mt: 0.2,
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontFamily: HEADING_FONT,
                                fontSize: 10.5,
                                fontWeight: 700,
                                letterSpacing: "0.5px",
                                color: "#f59e0b",
                              }}
                            >
                              {issue.type}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: HEADING_FONT,
                                fontSize: 12.5,
                                color: colors.text,
                                lineHeight: 1.4,
                              }}
                            >
                              {issue.message}
                            </Typography>
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                  )}

                  {/* Actions */}
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    sx={{ mt: 2 }}
                  >
                    <Button
                      size="small"
                      onClick={() => handleScanOne(m.domainId)}
                      disabled={scanningId === m.domainId}
                      startIcon={
                        scanningId === m.domainId ? (
                          <CircularProgress size={14} sx={{ color: "#10b981" }} />
                        ) : (
                          <RadarIcon sx={{ fontSize: 16 }} />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12.5,
                        px: 1.5,
                        borderRadius: 1.5,
                        color: "#10b981",
                        background: "rgba(16, 185, 129, 0.1)",
                        border: "1px solid rgba(16, 185, 129, 0.25)",
                        "&:hover": { background: "rgba(16, 185, 129, 0.18)" },
                        "&.Mui-disabled": { color: "rgba(16,185,129,0.6)" },
                      }}
                    >
                      {scanningId === m.domainId ? "Scanning…" : "Re-scan"}
                    </Button>
                    <Button
                      size="small"
                      component="a"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12.5,
                        px: 1.5,
                        borderRadius: 1.5,
                        color: "#3B82F6",
                        background: "rgba(59, 130, 246, 0.1)",
                        border: "1px solid rgba(59, 130, 246, 0.25)",
                        "&:hover": { background: "rgba(59, 130, 246, 0.18)" },
                      }}
                    >
                      Open site
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                      onClick={() => navigate(`/domains/${m.domainId}/edit`)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12.5,
                        px: 1.5,
                        borderRadius: 1.5,
                        color: "#f59e0b",
                        background: "rgba(245, 158, 11, 0.1)",
                        border: "1px solid rgba(245, 158, 11, 0.25)",
                        "&:hover": { background: "rgba(245, 158, 11, 0.18)" },
                      }}
                    >
                      Edit domain
                    </Button>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {/* Pagination footer */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            spacing={2}
            sx={{
              width: "100%",
              justifyContent: "space-between",
              px: 3,
              py: 2,
              borderTop: `1px solid ${colors.cardBorder}`,
              background: colors.hover,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
              Showing{" "}
              <Box component="span" sx={{ fontWeight: 700, color: colors.text }}>
                {rangeStart}–{rangeEnd}
              </Box>{" "}
              of{" "}
              <Box component="span" sx={{ fontWeight: 700, color: colors.text }}>
                {total}
              </Box>
            </Typography>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Select
                size="small"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                MenuProps={{
                  anchorOrigin: { vertical: "top", horizontal: "left" },
                  transformOrigin: { vertical: "bottom", horizontal: "left" },
                  PaperProps: {
                    sx: {
                      mb: 0.5,
                      minWidth: 72,
                      borderRadius: 2,
                      background: colors.menuBg,
                      border: `1px solid ${colors.cardBorder}`,
                      boxShadow: colors.shadowLg,
                      "& .MuiList-root": { py: 0.5 },
                      "& .MuiMenuItem-root": {
                        fontSize: 13,
                        color: colors.text,
                        mx: 0.5,
                        my: 0.25,
                        minHeight: 32,
                        borderRadius: 1,
                        justifyContent: "center",
                        "&:hover": { background: colors.hover },
                        "&.Mui-selected": {
                          background: "rgba(59, 130, 246, 0.14)",
                          color: "#3B82F6",
                          fontWeight: 700,
                          "&:hover": { background: "rgba(59, 130, 246, 0.2)" },
                        },
                      },
                    },
                  },
                }}
                sx={{
                  mr: 0.5,
                  height: 32,
                  minWidth: 62,
                  fontSize: 13,
                  fontWeight: 700,
                  color: colors.text,
                  background: colors.cardBg,
                  borderRadius: 2,
                  transition: "border-color 0.15s ease, background 0.15s ease",
                  "& .MuiSelect-select": {
                    py: 0,
                    pl: 1.5,
                    pr: "28px !important",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.cardBorder,
                  },
                  "&:hover": { background: colors.hover },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.cardBorderHover,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3B82F6",
                    borderWidth: "1px",
                  },
                  "& .MuiSvgIcon-root": { color: colors.textMuted, right: 4 },
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>
                    {n}
                  </MenuItem>
                ))}
              </Select>

              <Tooltip title="First page">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === 1}
                    onClick={() => setPage(1)}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": { background: colors.cardBg, color: "#3B82F6" },
                    }}
                  >
                    <FirstPageIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Previous">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": { background: colors.cardBg, color: "#3B82F6" },
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>

              {pageList.map((p, i) =>
                p === "..." ? (
                  <Box
                    key={`ellipsis-${i}`}
                    sx={{
                      px: 1,
                      fontSize: 13,
                      color: colors.textFaint,
                    }}
                  >
                    …
                  </Box>
                ) : (
                  <Box
                    key={p}
                    component="button"
                    onClick={() => setPage(p)}
                    sx={{
                      minWidth: 32,
                      height: 32,
                      px: 1,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor:
                        p === safePage ? "#3B82F6" : "transparent",
                      background:
                        p === safePage
                          ? "rgba(59, 130, 246, 0.12)"
                          : "transparent",
                      color: p === safePage ? "#3B82F6" : colors.text,
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        background:
                          p === safePage
                            ? "rgba(59, 130, 246, 0.18)"
                            : colors.cardBg,
                      },
                    }}
                  >
                    {p}
                  </Box>
                )
              )}

              <Tooltip title="Next">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === totalPages}
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    sx={{
                      color: colors.textMuted,
                      "&:hover": { background: colors.cardBg, color: "#3B82F6" },
                    }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Last page">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === totalPages}
                    onClick={() => setPage(totalPages)}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": { background: colors.cardBg, color: "#3B82F6" },
                    }}
                  >
                    <LastPageIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </FlatCard>
      )}
    </DashboardLayout>
  );
}

export default DomainMonitors;
